import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import * as models from 'smg/admin/models';
import axios from 'smg/axios';
import { ShowError } from 'smg/errors';
import { DollarFormat } from 'smg/util';

export interface BalanceHandle {
    // Re-fetch the resolved customer's available balance. Empty userID hides
    // the section and clears the applied amount.
    refresh(userID: string): void;
    // Forward-looking snapshot the future submit handler will fold into the
    // /orders POST body. `applied` is the user's raw input clamped to the
    // available balance — server-side will re-clamp against the live cart
    // total at place-order time so we don't apply more credit than the cart
    // actually owes. `useBalance` distinguishes "admin chose No" (don't
    // apply, deliberate) from "admin defaulted Yes but didn't enter
    // anything" (oversight — the future submit handler treats it as a
    // validation error).
    snapshot(): BalanceDraft;
}

export interface BalanceDraft {
    applied: InstanceType<typeof Big>;
    useBalance: boolean;
}

export interface BalanceDeps {
    // Returns the cart total before the balance credit is subtracted — i.e.
    // what the cart owes if the customer doesn't redeem any balance. Used to
    // clamp the "Apply $X" link's suggested amount. Deliberately doesn't drive
    // the applied value automatically: per design, only user input moves the
    // applied amount so shipping/tax/discount changes don't fight the input.
    getTotalBeforeBalance: () => InstanceType<typeof Big>;
    // Fires whenever the user-input applied amount changes (typing or Apply
    // click). The page wires this into summary's setBalanceApplied so the
    // sidebar row + grand total reflect the input.
    onAppliedChange: (applied: InstanceType<typeof Big>) => void;
    // Subscribe-to-total-change hook so the Apply link's suggested amount
    // tracks shipping/promo/exempt changes — clicking should always fill the
    // input with the right amount for the current total.
    onTotalChange: (cb: () => void) => void;
}

export function SetupBalance(deps: BalanceDeps): BalanceHandle {
    const $section = document.getElementById('checkout-section-balance') as HTMLElement;
    const $available = document.getElementById('balance-available') as HTMLElement;
    const $input = document.getElementById('balance-input') as HTMLInputElement;
    const $apply = document.getElementById('balance-apply') as HTMLButtonElement;
    const $applyAmount = document.getElementById('balance-apply-amount') as HTMLElement;
    const $clear = document.getElementById('balance-clear') as HTMLButtonElement;
    const $useYes = document.getElementById('balance-use-yes') as HTMLInputElement;
    const $useNo = document.getElementById('balance-use-no') as HTMLInputElement;
    const $amountCollapse = document.getElementById('balance-amount-collapse') as HTMLFieldSetElement;

    const amountCollapse = bootstrap.Collapse.getOrCreateInstance($amountCollapse, { toggle: false });

    let available = Big(0);
    // Increments on every refresh() so a fast customer switch can discard a
    // stale in-flight response.
    let fetchSeq = 0;

    function parseInput(): InstanceType<typeof Big> {
        const v = parseFloat($input.value);
        if (isNaN(v) || v <= 0) {
            return Big(0);
        }
        return Big(v);
    }

    // The amount the cart can absorb right now: min(available, totalOwed).
    // Apply click fills the input with this; the link is hidden when there's
    // nothing useful to apply.
    function applyCap(): InstanceType<typeof Big> {
        const cap = deps.getTotalBeforeBalance();
        const clamped = cap.lt(0) ? Big(0) : cap;
        return available.gt(clamped) ? clamped : available;
    }

    function renderApplyLink() {
        // No-side: input is hidden and applied is forced to $0, so the link
        // would be a no-op. Keep it suppressed regardless of cap.
        if (!$useYes.checked) {
            $apply.classList.add('d-none');
            return;
        }
        const max = applyCap();
        if (max.lte(0)) {
            $apply.classList.add('d-none');
            return;
        }
        $applyAmount.textContent = DollarFormat(max);
        $apply.classList.remove('d-none');
    }

    function effectiveApplied(): InstanceType<typeof Big> {
        // "No" means deliberately skip the credit — force applied to $0
        // regardless of what's typed in the input. Keeps the row hidden and
        // the grand total honest without depending on the admin clearing
        // the field first.
        if (!$useYes.checked) {
            return Big(0);
        }
        const raw = parseInput();
        return raw.gt(available) ? available : raw;
    }

    function notify() {
        deps.onAppliedChange(effectiveApplied());
    }

    function syncRadio() {
        if ($useYes.checked) {
            amountCollapse.show();
            // Fieldset gates the balance-input's `required` so HTML5
            // validation only kicks in when the admin actually opted into
            // applying balance.
            $amountCollapse.disabled = false;
        } else {
            amountCollapse.hide();
            $amountCollapse.disabled = true;
            // The native is-invalid marker on balance-input survives the
            // hide otherwise — clearinvalid drops it so the next "Yes"
            // doesn't reveal a stale red error.
            $input.dispatchEvent(new Event('clearinvalid'));
        }
        renderApplyLink();
        notify();
    }

    function resetRadioToDefault() {
        $useYes.checked = true;
        $useNo.checked = false;
        amountCollapse.show();
        // syncRadio's No-branch leaves the fieldset disabled. Switching
        // back to Yes via Clear (or any caller) has to re-enable it so the
        // visible required input participates in validation again. Without
        // this, native validation silently skips a visible required field.
        $amountCollapse.disabled = false;
    }

    $input.addEventListener('input', () => notify());
    $apply.addEventListener('click', () => {
        const max = applyCap();
        if (max.lte(0)) {
            return;
        }
        $input.value = max.toFixed(2);
        // Programmatic .value = doesn't fire input/change, so a stale
        // is-invalid from a prior place-order attempt would linger even
        // though we just made the field satisfy `required`.
        $input.dispatchEvent(new Event('clearinvalid'));
        notify();
    });
    $clear.addEventListener('click', () => {
        $input.value = '';
        resetRadioToDefault();
        notify();
    });
    $useYes.addEventListener('change', syncRadio);
    $useNo.addEventListener('change', syncRadio);

    deps.onTotalChange(() => renderApplyLink());

    async function fetchBalance(userID: string) {
        const mySeq = ++fetchSeq;
        // Reset to a safe baseline before awaiting anything. Otherwise a
        // slow or failing lookup for customer B could leave customer A's
        // balance applied to B's cart — both in this section's input and
        // in summary's totals math.
        available = Big(0);
        $section.classList.add('d-none');
        $input.value = '';
        resetRadioToDefault();
        // Section hidden means the required balance-input must not block
        // form validation — disable the fieldset so HTML5 skips its
        // descendants. resetRadioToDefault re-enables the fieldset (Clear
        // path needs that), so the section-hidden override has to come
        // AFTER it. We flip back to enabled only if the section becomes
        // visible with a non-zero balance.
        $amountCollapse.disabled = true;
        notify();
        if (!userID) {
            return;
        }
        try {
            const resp = await axios.get('/users', {
                params: {
                    'q': `id:${userID}`,
                    'data': 'balance',
                    'max': 1,
                },
            });
            if (mySeq !== fetchSeq) {
                return;
            }
            const user = models.UsersHyejooResponse.fromJSON(resp.data).users[0];
            // Negative balance means the user owes us (outstanding charge) —
            // not a credit, so don't offer to "apply" it. Treat as $0.
            const raw = user?.balance ?? Big(0);
            const next = raw.lte(0) ? Big(0) : raw;
            if (next.gt(0)) {
                available = next;
                $available.textContent = DollarFormat(available);
                $input.max = available.toFixed(2);
                $section.classList.remove('d-none');
                // Section now visible — restore validation gating to track
                // the radio. Default is Yes, so the input is required.
                $amountCollapse.disabled = !$useYes.checked;
                renderApplyLink();
            }
            // Else: the pre-await reset already cleared everything, no
            // additional state churn needed.
        } catch (err) {
            if (mySeq !== fetchSeq) {
                return;
            }
            ShowError(err);
        }
    }

    return {
        refresh: userID => void fetchBalance(userID),
        snapshot: () => ({
            applied: effectiveApplied(),
            useBalance: $useYes.checked,
        }),
    };
}
